import { BaseScheduleShiftViewModel } from '../../../entities/BaseScheduleShift/BaseScheduleShift'
import UpToAndIncludingDate from '../../../helpers/date/UpToAndIncludingDate'
import { AddShiftFormData, EditShiftFormData, ShiftPlanningType, ShiftPlanningViewModel, ShiftType } from '../../../models'
import compareShiftPlanningUsersAlphabetically from '../../../services/ShiftPlanningService/compareShiftPlanningUsersAlphabetically'
import { formatDate, getDayNumberFromDate, parseTime } from '../../helpers/date'
import { DateFormat } from '../../types/dateFormatTypes'
import { transformBaseScheduleShiftPlanning } from '../BaseScheduleShiftPlanning/BaseScheduleShiftPlanningTransformers'
import { Department, DepartmentIndexV3Resource } from '../Department/Department'
import { transformToPeriod } from '../Period/PeriodTransformers'
import {
    BaseScheduleShift,
    BaseScheduleShiftDetailResource,
    BaseScheduleShiftFormData,
    BaseScheduleShiftV3Resource,
    CreateBaseScheduleShiftRequest,
    EditBaseScheduleShiftTimeFormData,
    PatchBaseScheduleShiftRequest,
} from './BaseScheduleShift'

const transformV3Department = (
    department: DepartmentIndexV3Resource | null,
): Department | undefined => {
    if (! department) {
        return undefined
    }

    return {
        id: department.uuid,
        name: department.name,
        departmentGroup: department.groupName
            ? { id: department.groupName, name: department.groupName }
            : undefined,
    }
}

export const transformBaseScheduleShift = (resource: BaseScheduleShiftV3Resource): BaseScheduleShift => ({
    id: resource.uuid,
    weekNumber: resource.weekNumber,
    peopleLimit: resource.peopleLimit,
    assignedCount: resource.assignedCount,
    dayNumber: resource.dayNumber,
    department: transformV3Department(resource.department),
    period: transformToPeriod(
        parseTime(resource.start ?? '00:00'),
        parseTime(resource.end ?? '00:00'),
    ),
    shiftPlannings: resource.plannings.map(transformBaseScheduleShiftPlanning),
})

export const transformBaseScheduleShiftDetail = (
    resource: BaseScheduleShiftDetailResource,
): BaseScheduleShift => ({
    ...transformBaseScheduleShift(resource),
    baseScheduleId: resource.baseScheduleUuid,
})

export const transformEditBaseScheduleShiftTimeFormDataToPatchRequest = (
    formData: EditBaseScheduleShiftTimeFormData,
): PatchBaseScheduleShiftRequest => ({
    start: formatDate(formData.period.start, DateFormat.hoursMinutes),
    end: formatDate(formData.period.end, DateFormat.hoursMinutes),
})

export const transformAddBaseScheduleShiftFormDataToV3Request = (
    formData: BaseScheduleShiftFormData,
    baseScheduleId: string,
    weekNumber = 1,
): CreateBaseScheduleShiftRequest => ({
    baseScheduleUuid: baseScheduleId,
    departmentUuid: formData.department,
    weekNumber,
    dayNumber: getDayNumberFromDate(formData.date),
    start: formData.startTime,
    end: formData.endTime,
    peopleLimit: formData.peopleLimit || 0,
})

export const transformEditBaseScheduleShiftFormDataToV3PatchRequest = (
    formData: EditShiftFormData,
): PatchBaseScheduleShiftRequest => ({
    departmentUuid: formData.department,
    dayNumber: getDayNumberFromDate(formData.date),
    start: formData.startTime,
    end: formData.endTime,
    peopleLimit: formData.peopleLimit,
})

export const transformBaseScheduleShiftViewModelToV3CopyRequest = (
    shift: BaseScheduleShiftViewModel,
): CreateBaseScheduleShiftRequest => ({
    baseScheduleUuid: shift.baseScheduleId || '',
    departmentUuid: shift.department?.id || '',
    weekNumber: shift.weekNumber,
    dayNumber: shift.dayNumber,
    start: formatDate(shift.start, DateFormat.hoursMinutes),
    end: formatDate(shift.end.date, DateFormat.hoursMinutes),
    peopleLimit: shift.peopleLimit || 0,
})

export const transformAddShiftFormDataToV3Request = (
    formData: AddShiftFormData,
    baseScheduleId: string,
    weekNumber = 1,
): CreateBaseScheduleShiftRequest => ({
    baseScheduleUuid: baseScheduleId,
    departmentUuid: formData.department,
    weekNumber,
    dayNumber: getDayNumberFromDate(formData.date),
    start: formData.startTime,
    end: formData.endTime,
    peopleLimit: formData.peopleLimit || 0,
})

export const transformBaseScheduleShiftToViewModel = (
    shift: BaseScheduleShift,
): BaseScheduleShiftViewModel => ({
    type: ShiftType.baseSchedule,
    id: shift.id,
    start: shift.period.start,
    end: new UpToAndIncludingDate(shift.period.end),
    dayNumber: shift.dayNumber,
    weekNumber: shift.weekNumber,
    peopleLimit: shift.peopleLimit,
    department: shift.department ? {
        type: 'departments',
        id: shift.department.id,
        name: shift.department.name,
    } : undefined,
    shiftPlannings: shift.shiftPlannings
        .map((planning): ShiftPlanningViewModel => ({
            type: ShiftPlanningType.baseSchedule,
            id: planning.id,
            color: planning.color,
            customStartTime: planning.customStartTime,
            start: planning.period.start,
            end: new UpToAndIncludingDate(planning.period.end),
            planned: planning.planned,
            comments: [],
            user: {
                id: planning.user.id,
                type: 'users',
                firstname: planning.user.fullName.split(' ')[0] || '',
                lastname: planning.user.fullName.split(' ').slice(1).join(' ') || '',
                employmentType: planning.user.employmentType,
                person: {
                    isCompanyMedic: planning.user.companyMedic,
                },
            } as ShiftPlanningViewModel['user'],
        }))
        .sort(compareShiftPlanningUsersAlphabetically),
    baseScheduleId: shift.baseScheduleId,
})
